import { merge as tsDeepmerge } from "ts-deepmerge";
import {
  isLoading,
  LOADING,
  Loading,
} from "@coworker/apprestructured/src/task-management/loading";

/**
 * Get a nested property of type T of an object using dot notation.
 * @example getByPath({ a: { b: "hej!" } }, "a.b"); => "hej!"
 * @example getByPath(LOADING, "a.b"); => LOADING
 *
 * @param obj an object or LOADING
 * @param path dot notation for the nested property
 */
export function getByPath<T>(
  obj: Record<string, unknown> | Loading,
  path: string
): T | Loading {
  if (isLoading(obj)) {
    return LOADING;
  }
  const keys = path.split(".");
  let result: unknown = obj;

  for (const key of keys) {
    if (!isRecord(result)) {
      throw new Error(`Unable to get '${path}' from '${JSON.stringify(obj)}'`);
    }
    result = (result as Record<string, unknown>)[key];
  }

  return result as T;
}

/**
 * Create an object with 'value' set to the dot noted 'path' field.
 * @example setByPath("a.b", 2); => { a: { b: 2 } }
 * @example setByPath("a.b", 3, { a: { b: 1, c: 2 } }); => { a: { b: 3, c: 2 } }
 *
 * @param path dot notation for the field
 * @param value the value to set
 * @param obj the object to begin with, or undefined
 */
export function setByPath(
  path: string,
  value: unknown,
  obj: Record<string, unknown> = {}
): Record<string, unknown> {
  const keys = path.split(".");
  if (keys.length === 0) {
    return obj;
  }

  const [head, ...rest] = keys;

  if (!head) {
    return obj;
  }

  if (rest.length === 0) {
    return {
      ...obj,
      [head]: value,
    };
  }

  const nested = obj[head];
  const updatedNested = setByPath(
    rest.join("."),
    value,
    isRecord(nested) ? nested : {}
  );

  return {
    ...obj,
    [head]: updatedNested,
  };
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function merge(
  obj1: Record<string, unknown>,
  obj2: Record<string, unknown>
) {
  return tsDeepmerge.withOptions({ mergeArrays: false }, obj1, obj2);
}
